import type { HistoryVersion } from '@ast-data/shared';

/**
 * What changed between two versions — INCLUDING the fields the dialog does not
 * diff. A version only exists because the server saw a different snapshot, so
 * "keine Änderungen" only ever meant "nothing changed among the fields this
 * dialog lists". This module finds the rest and describes their values well
 * enough to name the change without printing a UUID or a whole Konva scene.
 */

/** Metadata the wire wraps around a snapshot — never content. */
const WIRE_META_FIELDS = ['historyId', 'entityId', 'savedAt', 'changedBy'];

/** Longest value a summary still prints verbatim. */
const SUMMARY_MAX = 60;

/** Longest single entry that lets an array print its values instead of a count. */
const ENTRY_MAX = 20;

/**
 * A value read as a LIST, however it happens to be stored: the reference arrays
 * travel as a real array on one push and as their JSON text on another.
 * Comparing those two forms as plain text reports a change nobody made.
 */
function asList(value: unknown): unknown[] | null {
    if (Array.isArray(value)) {
        return value;
    }
    if (typeof value === 'string' && value.trimStart().startsWith('[')) {
        try {
            const parsed: unknown = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : null;
        } catch {
            return null;
        }
    }
    return null;
}

/**
 * True for a value that READS as nothing — absent, null, '', an empty list, or
 * a list whose entries are themselves nothing. The comparison and the wording
 * must agree on this, or the dialog reports a change and then describes both
 * sides of it as "(leer)": a field added to the schema later is absent in the
 * old versions and `[]` in the new ones.
 */
function isNothing(value: unknown): boolean {
    if (value === undefined || value === null || value === '') {
        return true;
    }
    const list = asList(value);
    return list !== null && list.every(isNothing);
}

/** The recursive part: unambiguous text for anything nested in a value. */
function canonicalPart(value: unknown): string {
    if (Array.isArray(value)) {
        return `[${value.map(canonicalPart).join(',')}]`;
    }
    if (value && typeof value === 'object') {
        const record = value as Record<string, unknown>;
        const body = Object.keys(record)
            .sort()
            .map(
                (key) => `${JSON.stringify(key)}:${canonicalPart(record[key])}`
            )
            .join(',');
        return `{${body}}`;
    }
    return JSON.stringify(value) ?? '';
}

/**
 * Order-independent text for one value, so two snapshots compare by CONTENT.
 * Absent, null and '' are the same nothing — the dialog renders them alike, and
 * a field that merely appeared in the snapshot is not a change anybody made.
 */
export function canonicalValue(value: unknown): string {
    if (isNothing(value)) {
        return '';
    }
    const list = asList(value);
    if (list) {
        return canonicalPart(list);
    }
    /**
     * A whole field that IS a string is its own canonical form; quoting it
     * would copy it, and a sketch scene is a hundred kilobytes of JSON. Nested
     * strings keep their quotes (see canonicalPart), where the ambiguity
     * between ['a,b'] and ['a','b'] would be real.
     */
    if (typeof value === 'string') {
        return value;
    }
    return canonicalPart(value);
}

/**
 * True for a snapshot key whose change says nothing on its own: a singular
 * `*Id` (it either says where the record lives or mirrors a NAME field beside
 * it), or a plural `*Ids` that mirrors a name list in the same snapshot.
 *
 * A plural WITHOUT such a sibling stays: a Planum's "identisch mit" exists as
 * ids and nothing else, so hiding those would make the change invisible.
 */
function isMirrorId(key: string, snapshotKeys: Set<string>): boolean {
    if (/Id$/.test(key)) {
        return true;
    }
    return /Ids$/.test(key) && snapshotKeys.has(key.slice(0, -3));
}

/**
 * The changed snapshot fields that are not among `trackedKeys`, in the order
 * the snapshot records them, newest version first.
 */
export function otherChangedKeys(
    version: HistoryVersion,
    previousVersion: HistoryVersion,
    trackedKeys: readonly string[]
): string[] {
    const skip = new Set([...WIRE_META_FIELDS, ...trackedKeys]);
    const keys: string[] = [];
    for (const key of Object.keys(version)) {
        keys.push(key);
    }
    for (const key of Object.keys(previousVersion)) {
        if (!keys.includes(key)) {
            keys.push(key);
        }
    }
    const snapshotKeys = new Set(keys);
    return keys.filter(
        (key) =>
            !skip.has(key) &&
            !isMirrorId(key, snapshotKeys) &&
            canonicalValue(version[key]) !==
                canonicalValue(previousVersion[key])
    );
}

/**
 * How to word a value the dialog does not diff. Structured rather than a string
 * so the wording (and its plurals) stay in the catalogs, not in here.
 */
export type ValueSummary =
    | { kind: 'empty' }
    | { kind: 'text'; text: string }
    | { kind: 'boolean'; value: boolean }
    | { kind: 'items'; count: number }
    | { kind: 'chars'; count: number }
    | { kind: 'changed' };

/**
 * A short stand-in for any snapshot value. Long text, id arrays and embedded
 * objects are DESCRIBED, never printed: a row of UUIDs or a serialized sketch
 * names nothing.
 */
export function summarizeValue(value: unknown): ValueSummary {
    // Same nothing as the comparison uses (see isNothing).
    if (isNothing(value)) {
        return { kind: 'empty' };
    }
    if (typeof value === 'boolean') {
        return { kind: 'boolean', value };
    }
    if (typeof value === 'number') {
        return { kind: 'text', text: String(value) };
    }
    const list = asList(value);
    if (list) {
        const entries = list.map((entry) =>
            typeof entry === 'string' || typeof entry === 'number'
                ? String(entry)
                : null
        );
        const text = entries.every(
            (entry) => entry !== null && entry.length <= ENTRY_MAX
        )
            ? entries.join(', ')
            : null;
        if (text !== null && text.length <= SUMMARY_MAX) {
            return { kind: 'text', text };
        }
        return { kind: 'items', count: list.length };
    }
    if (typeof value === 'object') {
        return { kind: 'changed' };
    }
    const text = String(value);
    return text.length <= SUMMARY_MAX
        ? { kind: 'text', text }
        : { kind: 'chars', count: text.length };
}
